// src/components/WeeklyPlanModal.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import { CaretLeft, CaretRight, Plus, X } from "phosphor-react";

const getMonday = (d0 = new Date()) => {
  const d = new Date(d0.getFullYear(), d0.getMonth(), d0.getDate());
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

const formatRangeDate = (date) => {
  const d = new Date(date);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

export default function WeeklyPlanModal({ open, onClose, keySuffix }) {
  const [offsetWeeks, setOffsetWeeks] = useState(0);

  const baseMonday = useMemo(() => getMonday(new Date()), []);

  const viewMonday = useMemo(
    () => addDays(baseMonday, offsetWeeks * 7),
    [baseMonday, offsetWeeks]
  );

  const storageKey = useMemo(
    () => `tp:weekplan:${keySuffix}:${ymd(viewMonday)}`,
    [keySuffix, viewMonday]
  );

  const days = useMemo(
    () => ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    []
  );

  const dayDateLabels = useMemo(
    () =>
      Object.fromEntries(
        days.map((d, i) => {
          const date = addDays(viewMonday, i);
          return [d, `${date.getMonth() + 1}/${date.getDate()}`];
        })
      ),
    [days, viewMonday]
  );

  const placeholderMap = {
    Mon: "6AM Solidcore",
    Tue: "7:30PM Guitar lesson",
    Wed: "6AM Solidcore",
    Thu: "8PM Pottery",
    Fri: "6AM Solidcore",
    Sat: "2PM Reading",
    Sun: "6:30PM Cooking class @ Eataly",
  };

  const emptyDay = () => ["", ""];

  const emptyPlan = useMemo(
    () => Object.fromEntries(days.map((d) => [d, emptyDay()])),
    [days]
  );

  const [plan, setPlan] = useState(emptyPlan);

  const focusRef = useRef(null);
  const inputRefs = useRef({});

  const setInputRef = (day, idx) => (el) => {
    if (!inputRefs.current[day]) inputRefs.current[day] = {};
    inputRefs.current[day][idx] = el;
  };

  const autoResize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    if (!open) return;

    try {
      const raw = localStorage.getItem(storageKey);

      if (!raw) {
        setPlan(emptyPlan);
        return;
      }

      const parsed = JSON.parse(raw);
      const next = {};

      for (const d of days) {
        const v = parsed?.[d];

        if (Array.isArray(v)) next[d] = v.length ? v : emptyDay();
        else if (typeof v === "string") next[d] = v ? [v] : emptyDay();
        else next[d] = emptyDay();
      }

      setPlan(next);
    } catch {
      setPlan(emptyPlan);
    }
  }, [open, storageKey, emptyPlan, days]);

  useEffect(() => {
    const f = focusRef.current;
    if (!f) return;

    const el = inputRefs.current?.[f.day]?.[f.idx];

    if (el) {
      el.focus();

      try {
        el.setSelectionRange(f.caret, f.caret);
      } catch {}

      autoResize(el);
    }
  }, [plan]);

  const save = () => {
    const cleaned = {};

    for (const d of days) {
      const list = (plan[d] || []).map((s) => (s ?? "").trim());
      while (list.length && !list[list.length - 1]) list.pop();
      cleaned[d] = list;
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify(cleaned));
    } catch {}

    onClose?.();
  };

  const setDayItems = (day, updater) =>
    setPlan((p) => {
      const base = Array.isArray(p[day]) ? [...p[day]] : emptyDay();
      const next = typeof updater === "function" ? updater(base) : updater;
      return { ...p, [day]: next };
    });

  const DayCard = ({ day, dateLabel }) => {
    const items = plan[day] || emptyDay();

    const addLine = () => setDayItems(day, (arr) => [...arr, ""]);

    const onChangeItem = (idx, e) => {
      const value = e.target.value;

      focusRef.current = {
        day,
        idx,
        caret: e.target.selectionStart ?? value.length,
      };

      setDayItems(day, (arr) => {
        const next = [...arr];
        next[idx] = value;

        if ((next[next.length - 1] ?? "").trim()) next.push("");

        return next;
      });

      autoResize(e.target);
    };

    const onKeyDown = (idx, e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        focusRef.current = { day, idx: idx + 1, caret: 0 };
        addLine();
      }
    };

    return (
      <div
        className="
          group rounded-[22px] border border-blue-100/80
          bg-white/90 shadow-[0_6px_0px_rgba(37,99,235,0.96)]
          min-h-[178px] overflow-hidden
          transition hover:-translate-y-0.5 focus-within:shadow-[0_6px_0px_rgba(37,99,235,0.0)]
        "
      >
        <div className="px-3 pt-3 pb-2">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-blue-700 text-[13px] outfit-font font-extrabold tracking-[-0.01em] uppercase">
              {day}
            </div>

            <div className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-bold text-blue-400">
              {dateLabel}
            </div>
          </div>

          <div className="mt-2 h-[3px] w-7 rounded-full bg-blue-300/70" />
        </div>

        <div className="px-2.5 pb-2 space-y-1.5">
          {items.map((val, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <div className="mt-[12px] h-1.5 w-1.5 rounded-full bg-blue-300/80 shrink-0" />

              <textarea
                ref={(el) => {
                  setInputRef(day, i)(el);
                  if (el) autoResize(el);
                }}
                value={val}
                onChange={(e) => onChangeItem(i, e)}
                onKeyDown={(e) => onKeyDown(i, e)}
                placeholder={i === 0 ? placeholderMap[day] : "Add plan"}
                rows={1}
                className="
                  w-full resize-none overflow-hidden
                  rounded-xl border border-transparent bg-blue-50/45
                  px-2 py-1.5 text-[13px] leading-[18px] text-blue-950
                  outline-none transition
                  focus:border-blue-200 focus:bg-white focus:shadow-sm
                  whitespace-pre-wrap break-words
                  placeholder:text-blue-300
                "
              />
            </div>
          ))}
        </div>

        <div className="px-3 pb-3">
          <button
            type="button"
            onClick={addLine}
            className="
              inline-flex items-center gap-1.5 rounded-full
              border border-blue-100 bg-white px-2.5 py-1.5
              text-[11px] font-bold text-blue-500
              shadow-sm transition hover:bg-blue-50
            "
          >
            <Plus size={12} weight="bold" />
            Add
          </button>
        </div>
      </div>
    );
  };

  if (!open) return null;

  const weekStart = formatRangeDate(viewMonday);
  const weekEnd = formatRangeDate(addDays(viewMonday, 6));

  return (
    <div className="fixed inset-0 z-[150] grid place-items-center bg-black/35 overflow-y-auto p-0 md:p-6">
      <div
        className="
          relative w-[94vw] max-w-6xl overflow-hidden
          rounded-[28px] border border-blue-100/80
          bg-gradient-to-b from-blue-50 via-white to-white
          p-4 md:p-6 shadow-[0_30px_90px_rgba(15,23,42,0.22)]
        "
      >
        <button
          onClick={onClose}
          className="
            absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center
            rounded-full bg-white/80 text-blue-500 shadow-sm
            transition hover:bg-blue-200
          "
          aria-label="Close"
        >
          <X size={19} weight="bold" />
        </button>

        <div className="text-center">
          <div className="mt-1 flex items-center justify-center">
            <button
              type="button"
              onClick={() => setOffsetWeeks((n) => n - 1)}
              className="grid h-9 w-9 place-items-center rounded-full text-blue-400 transition hover:bg-white"
              aria-label="Previous week"
              title="Previous week"
            >
              <CaretLeft size={25} weight="fill" />
            </button>

            <h2 className="mx-2 text-blue-600 font-black leading-none tracking-[-0.04em] text-[32px]">
              Weekly Plan
            </h2>

            <button
              type="button"
              onClick={() => setOffsetWeeks((n) => n + 1)}
              className="grid h-9 w-9 place-items-center rounded-full text-blue-400 transition hover:bg-white"
              aria-label="Next week"
              title="Next week"
            >
              <CaretRight size={25} weight="fill" />
            </button>
          </div>

          <div className="mt-1 outfit-font text-xs md:text-sm font-bold text-blue-400">
            {weekStart} – {weekEnd}
          </div>
        </div>

        <div className="mt-5">
          <div className="md:hidden space-y-2">
            {days.map((d) => (
              <DayCard key={`m-${d}`} day={d} dateLabel={dayDateLabels[d]} />
            ))}
          </div>

          <div className="hidden md:grid md:grid-cols-7 md:gap-2.5">
            {days.map((d) => (
              <DayCard key={`d-${d}`} day={d} dateLabel={dayDateLabels[d]} />
            ))}
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="
                px-4 py-2 rounded-full border border-blue-100 bg-white
                text-blue-500 text-sm font-bold shadow-sm
                transition hover:bg-blue-50
              "
            >
              Cancel
            </button>

            <button
              onClick={save}
              className="
                px-5 py-2 rounded-full bg-blue-600
                text-white text-sm font-bold shadow-sm
                transition hover:bg-blue-700
              "
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}